import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useExpertContext } from '../context/ExpertContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { jordanGovernorates } from '../data/jordanGovernorates';
import { useLanguage } from '../context/LanguageContext';

const { FiUser, FiMail, FiMapPin, FiBook, FiInfo, FiCheckCircle, FiAlertCircle, FiLoader } = FiIcons;

const RegisterExpert = () => {
  const navigate = useNavigate();
  const { addExpert } = useExpertContext();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    governorate: '',
    city: '',
    bio: '',
    support_topics: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [consent, setConsent] = useState(false);

  const supportTopics = [
    'Classroom Setup', 'Lesson Planning', 'STEM Integration', 
    'Programming Basics', 'World Building', 'Collaborative Projects'
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleTopicToggle = (topic) => {
    setFormData(prev => ({
      ...prev,
      support_topics: prev.support_topics.includes(topic)
        ? prev.support_topics.filter(t => t !== topic)
        : [...prev.support_topics, topic]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      const selectedGov = jordanGovernorates.find(g => g.name === formData.governorate);
      const expertToSubmit = {
        ...formData,
        latitude: selectedGov?.visualX || 50,
        longitude: selectedGov?.visualY || 50
      };

      await addExpert(expertToSubmit);
      setIsSubmitted(true);
      setTimeout(() => navigate('/'), 4000);
    } catch (err) {
      console.error('Detailed Registration Error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="container mx-auto px-4 py-20 flex justify-center">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }}
          className="expert-card max-w-lg text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('register.successTitle')}</h2>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-left">
            <p className="text-blue-800 font-bold">{t('register.successStatus')}</p>
            <p className="text-blue-700 text-sm mt-1">{t('register.successMsg')}</p>
          </div>
          <p className="text-gray-500 text-sm animate-pulse">{t('register.redirect')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-black gradient-text mb-3">{t('register.title')}</h1>
        <p className="text-gray-600">{t('register.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-start space-x-3"
          >
            <SafeIcon icon={FiAlertCircle} className="text-xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Registration Failed</p>
              <p className="text-sm mt-1">{error}</p>
              {error.includes('fetch') && (
                <p className="text-xs mt-2 opacity-80">
                  Tip: If you are using Brave or have an ad-blocker like uBlock Origin, try disabling it for this site as it may block Supabase requests.
                </p>
              )}
            </div>
          </motion.div>
        )}

        <div className="expert-card grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2 flex items-center p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-800 text-sm">
            <SafeIcon icon={FiInfo} className="mr-3 text-lg flex-shrink-0" />
            <p>{t('register.info')}</p>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-bold text-gray-700 ml-1">{t('register.fullName')} *</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                name="full_name" 
                required 
                value={formData.full_name} 
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="e.g. Ahmad Al-Zubi"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-bold text-gray-700 ml-1">{t('register.email')} *</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="email" 
                name="email" 
                required 
                value={formData.email} 
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="teacher@edu.jo"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-bold text-gray-700 ml-1">{t('register.govLabel')} *</label>
            <div className="relative">
              <SafeIcon icon={FiMapPin} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select 
                name="governorate" 
                required 
                value={formData.governorate} 
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all bg-white appearance-none"
              >
                <option value="">Select Governorate...</option>
                {jordanGovernorates.map(g => (
                  <option key={g.name} value={g.name}>{g.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-bold text-gray-700 ml-1">{t('register.cityLabel')}</label>
            <input 
              name="city" 
              value={formData.city} 
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
              placeholder="e.g. Khalda, Amman"
            />
          </div>

          <div className="md:col-span-2 space-y-1">
            <label className="block text-sm font-bold text-gray-700 ml-1">{t('register.bio')}</label>
            <textarea 
              name="bio" 
              rows="3" 
              value={formData.bio} 
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all resize-none"
              placeholder="Tell us about your experience with Minecraft Education..."
            />
          </div>
        </div>

        <div className="expert-card">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiBook} className="mr-2 text-green-600" />
            {t('register.topics')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {supportTopics.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => handleTopicToggle(t)}
                className={`px-4 py-2 rounded-full border transition-all duration-200 text-sm font-medium ${
                  formData.support_topics.includes(t)
                    ? 'bg-green-600 text-white border-green-600 shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="expert-card bg-slate-50 border-dashed border-2 border-slate-200">
          <label className="flex items-start space-x-3 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={consent} 
              onChange={e => setConsent(e.target.checked)}
              className="mt-1 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded-lg transition-all"
              required
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
              {t('register.consent')}
            </span>
          </label>
        </div>

        <button 
          disabled={!consent || isSubmitting}
          className="w-full py-4 btn-primary text-black rounded-2xl font-black text-lg shadow-xl shadow-green-200/50 disabled:opacity-50 disabled:shadow-none transition-all active:scale-[0.98]"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiLoader} className="animate-spin text-xl" />
              <span>{t('register.processing')}</span>
            </div>
          ) : t('register.submit')}
        </button>
      </form>
    </div>
  );
};

export default RegisterExpert;